import React, { useState, useCallback, useRef } from 'react';
import useStore from '../../../../store';
import { HexAlphaColorPicker } from "react-colorful";
import './PuzzleSettings.css';
// Импортируем иконки или другие элементы, если нужны

// Перенесем функцию проверки условий сюда, чтобы ее можно было использовать и в read-only списке
const checkCondition = (condition, actualValue) => {
  if (condition && typeof condition === 'object' && !Array.isArray(condition)) {
    if ('$gte' in condition) return actualValue >= condition.$gte;
    if ('$lte' in condition) return actualValue <= condition.$lte;
    if ('$gt' in condition) return actualValue > condition.$gt;
    if ('$lt' in condition) return actualValue < condition.$lt;
    if ('$eq' in condition) return actualValue === condition.$eq;
    if ('$ne' in condition) return actualValue !== condition.$ne;
  }
  return actualValue === condition;
};

// Компонент для отображения одной настройки
function SettingItem({ setting, currentSettings, handleChange, openColorPicker }) {
  const value = currentSettings[setting.actualKey || setting.key] ?? setting.default ?? setting.min ?? '#FFFFFF';

  const handleSliderChange = (e) => {
    const newValue = Number(e.target.value);
    
    handleChange(setting.actualKey || setting.key, newValue, setting);
    if (setting.redraw) {
      useStore.setState({ puzzleRedraw: Date.now(), redrawOptions: setting.redraw });
    }
  };

  const handleToggleChange = (newValue) => {
    handleChange(setting.key, newValue, setting);
  };

  // <<< НОВЫЙ ОБРАБОТЧИК для чекбоксов >>>
  const handleCheckboxChange = (e) => {
    const key = setting.key;
    const newValue = e.target.checked;

    handleChange(setting.key, e.target.checked, setting);

    if (key === 'fullscreen') {
      if (newValue) {
        try { window.Telegram.WebApp.requestFullscreen(); } catch (e) {}
        try { document.documentElement.requestFullscreen().catch(()=>{}); } catch (e) {}
      } else {
        try { window.Telegram.WebApp.exitFullscreen(); } catch (e) {}
        try { if (document.fullscreenElement) document.exitFullscreen().catch(()=>{}); } catch (e) {}
      }
    }
  };

  // Проверяем условия для отображения настройки
  if (setting.conditions) {
    for (const key in setting.conditions) {
      if (!checkCondition(setting.conditions[key], currentSettings[key])) {
        return null; // Не рендерим, если условие не выполнено
      }
    }
  }

  switch (setting.type) {
    case 'slider':
      
      return (
        <div className="puzzle-setting-item slider-item">
          <label>{setting.label}: {setting.percent ? `${(value * 100).toFixed(0)}%` : value.toFixed(setting.step < 1 ? 2 : 0)}</label>
          <input
            type="range"
            min={setting.min}
            max={setting.max}
            step={setting.step}
            value={value}
            onChange={handleSliderChange}
            className={`puzzle-slider ${value >= setting.max ?  'expanded-range' : Math.abs(value - setting.default) <= setting.max * 0.1 ? 'default-range' : ''}`}
          />
        </div>
      );
    case 'toggle':
      // Используем кнопки вместо чекбокса для лучшего стиля
      return (
        <div className="puzzle-setting-item toggle-item">
          <label>{setting.label}</label>
          <div className="toggle-buttons">
            {setting.options.map(option => (
              <button
                key={option.value}
                className={`toggle-button ${value === option.value ? 'active' : ''}`}
                onClick={() => handleToggleChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    case 'color':
      return (
        <div className="puzzle-setting-item color-item" onClick={() => openColorPicker(setting.key)}>
          <label className="puzzle-settings-label" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <span className="puzzle-color-indicator" style={{ backgroundColor: value }}></span>
            <span>{setting.label}</span>
          </label>
        </div>
      );
    // <<< НОВЫЙ ТИП: ЧЕКБОКС >>>
    case 'checkbox':
      return (
        <div className="puzzle-setting-item checkbox-item">
          <label htmlFor={setting.key} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id={setting.key}
              checked={!!value} // Приводим к boolean
              onChange={handleCheckboxChange}
              style={{ marginRight: '8px' }} // Небольшой отступ
            />
            {setting.label}
          </label>
        </div>
      );
    // Добавь сюда другие типы настроек (чекбоксы, селекты), если понадобятся
    default:
      return null;
  }
}

// Основной компонент настроек
export function PuzzleSettings({ store }) {
  const { ref } = store;
  const { info, work, menu, telegram } = ref;
  const { user } = info;
  const { closeMenu } = menu.methods;
  const { debounceUserSets, debounceGameSets } = work.methods;
  const { game, } = info;

  // Выбранная категория настроек из ref.info.user.puzzleSetCat или по умолчанию "user"
  const [currentUserSettings, setCurrentUserSettings] = useState(ref.puzzle.config.user);
  const [currentGameSettings, setCurrentGameSettings] = useState(ref.puzzle.config.game);
  const [gameChanged, setGameChanged] = useState(false);
  const [userChanged, setUserChanged] = useState(false);


  // <<< Состояние для колорпикера >>>
  const [colorPickerState, setColorPickerState] = useState({
    isOpen: false,
    key: null,
  });
  const [pickedColor, setPickedColor] = useState('#FFFFFF'); // Временный цвет в пикере

  // Конфигурация настроек
  const userSettingsConfig = [
    { type: 'color', key: 'puzzleBackgroundColor', label: 'Цвет фона', default: '#FFFFFF',
      onSettingChange: (color) => {
        ref.puzzle.methods.toggleTheme(color);
      }
     },
    { type: 'checkbox', key: 'fullscreen', label: 'Во весь экран', default: false, redraw: {} },
    { type: 'checkbox', key: 'startAnimation', label: 'Стартовая анимация', default: true },
    { type: 'checkbox', key: 'debugMode', label: 'Режим отладки', default: false },
    { type: 'checkbox', key: 'debugInfo', label: 'Инфо отладки', default: false },
    { type: 'slider', key: 'touchDragOffsetY', label: 'Смещение от пальца', min: 0, max: 100, step: 1, default: 60,}, 
    { type: 'slider', key: 'trayGapFactor', label: 'Расстояние между пазлами', min: 0, max: 1, step: 0.01, default: 0.55, percent: true,}, 
    
    { type: 'slider', key: 'trayHeight', label: 'Высота ящика с пазлами', min: 0.2, max: 0.5, step: 0.01, default: 0.35, percent: true,
    }, 
  ];

  const gameSettingsConfig = [
    { type: 'slider', key: 'puzzlePieceCount', label: 'Количество кусочков', min: 4, max: 150, step: 1, default: 50 }, 
    { conditions: { puzzlePieceCount: { $gte: 150 } }, type: 'slider', key: 'puzzlePieceCountLarge', actualKey: 'puzzlePieceCount', label: 'Большой пазл', min: 150, max: 1000, step: 1, default: 50 }, 

    { type: 'slider', key: 'puzzlePreviewOpacity', label: 'Непрозрачность превью', min: 0, max: 1, step: 0.01, default: 0.23, redraw: {}, percent: true },
    { type: 'slider', key: 'snapToleranceFactor', label: 'Примагничивание', min: 0.1, max: 2, step: 0.01, default: 0.5, redraw: {}, percent: true },
    { type: 'checkbox', key: 'pieceRotation', label: 'Вращение пазлов', default: false, redraw: {} },
    { type: 'toggle', key: 'puzzleType', label: 'Тип пазла', options: [
        {value: 'jigsaw', label: 'Резные'},
        {value: 'square', label: 'Квадратные'},
      ], default: 'jigsaw'
    },
    { conditions: { puzzleType: 'jigsaw' }, type: 'slider', key: 'puzzleTabSize', label: 'Размер выступов', min: 5, max: 50, step: 1, default: 36 },
    { conditions: { puzzleType: 'jigsaw' }, type: 'slider', key: 'puzzleJitter', label: 'Искривление', min: 0, max: 10, step: 0.1, default: 0 },
  ];

  // Обработчик изменений для пользовательских настроек
  const handleUserSettingsChange = useCallback((key, value, setting) => {
    // Обновляем локальное состояние для немедленного отклика UI
    setCurrentUserSettings(prev => ({ ...prev, [key]: value }));
    ref.puzzle.config.user[key] = value;

    setUserChanged(true);
    debounceUserSets({ [key]: value });

    if (setting.onSettingChange) {
      setting.onSettingChange(value);
    }
    if (setting.redraw) {
      useStore.setState({ puzzleRedraw: Date.now(), redrawOptions: setting.redraw });
    }
  }, [debounceUserSets, ref.puzzle.config.user]);

  // Обработчик изменений для игровых настроек
  const handleGameSettingsChange = useCallback((key, value, setting) => {
    // Обновляем локальное состояние для немедленного отклика UI
    setCurrentGameSettings(prev => ({ ...prev, [key]: value }));
    ref.puzzle.config.game[key] = value;
    setGameChanged(true);

    debounceGameSets({ [key]: value });
    debounceUserSets({ [key]: value });

    if (setting.onSettingChange) {
      setting.onSettingChange(value);
    }
    if (setting.redraw) {
      useStore.setState({ puzzleRedraw: Date.now(), redrawOptions: setting.redraw });
    }
  }, [debounceGameSets, debounceUserSets, ref.puzzle.config.game]);

  // <<< Функции для колорпикера >>>
  const startColorPick = (key) => {
    const isUserSetting = userSettingsConfig.some(setting => setting.key === key);
    const currentConfig = isUserSetting ? currentUserSettings : currentGameSettings;
    
    setPickedColor(currentConfig[key] || '#FFFFFF'); // Устанавливаем текущий цвет в пикер
    setColorPickerState({ isOpen: true, key: key, isUserSetting });
  };

  const onColorPicking = (newColor) => {
    setPickedColor(newColor); // Обновляем временный цвет
    // Обновляем локальное состояние для немедленного отклика UI в индикаторе
    if (colorPickerState.isUserSetting) {
      setCurrentUserSettings(prev => ({ ...prev, [colorPickerState.key]: newColor }));
    } else {
      setCurrentGameSettings(prev => ({ ...prev, [colorPickerState.key]: newColor }));
    }
    // Можно добавить debounce для saveUserSets здесь, если нужно сохранять "на лету"
  };

  const endColorPick = () => {
    if (colorPickerState.key) {
      // Финально сохраняем выбранный цвет
      if (colorPickerState.isUserSetting) {
        debounceUserSets({ [colorPickerState.key]: pickedColor });
        ref.puzzle.config.user[colorPickerState.key] = pickedColor;
      } else {
        debounceGameSets({ [colorPickerState.key]: pickedColor });
        ref.puzzle.config.game[colorPickerState.key] = pickedColor;
      }

      setUserChanged(true);

      // Поиск настройки в соответствующем массиве
      const allSettings = [...userSettingsConfig, ...gameSettingsConfig];
      const setting = allSettings.find(setting => setting.key === colorPickerState.key);
      
      if (setting?.onSettingChange) {
        setting.onSettingChange(pickedColor);
      }
      // Закрываем пикер
      setColorPickerState({ isOpen: false, key: null, isUserSetting: false });
    }
  };
  
  const handleReinitialize = () => {

    ref.puzzle.methods.initializePuzzle();
    setGameChanged(false);
    ref.work.methods.hostAction('gameConfig', { gameSettings: ref.puzzle.config.game });

  };


  const handleCloseMenu = () => {
    closeMenu();
    if (gameChanged) {
      handleReinitialize();
    }
    if (userChanged) {
      ref.puzzle.methods.redraw();
      setUserChanged(false);
    }
  };
  // --- Конец функций для колорпикера ---

  // <<< Функция для форматирования значения для read-only >>>
  const formatReadOnlyValue = (setting, value) => {
    if (value === undefined || value === null) return 'N/A';

    switch (setting.type) {
      case 'slider':
        return setting.percent
          ? `${(value * 100).toFixed(0)}%`
          : value.toFixed(setting.step < 1 ? 2 : 0);
      case 'toggle':
        const option = setting.options.find(opt => opt.value === value);
        return option ? option.label : value;
      case 'checkbox':
        return value ? 'Вкл' : 'Выкл';
      case 'color':
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <span className="puzzle-color-indicator" style={{ backgroundColor: value }}></span>
            {value}
          </span>
        );
      default:
        return String(value);
    }
  };

  return (
    <>
      {/* Оверлей для закрытия меню по клику вне его */}
      <div className="puzzle-settings-overlay" onClick={handleCloseMenu}></div>
      {/* Само меню настроек */}
      <div className={`puzzle-settings-menu ${colorPickerState.isOpen ? 'blurred' : ''}`}>
        {/* Заголовок и переключатели категорий */}
        <div className="puzzle-settings-header">
          <h3 className="puzzle-settings-title">Настройки пазла</h3>
          <div className="puzzle-category-switcher">
            <button
              className={`puzzle-category-btn ${user.puzzleSetCat === 'user' ? 'active' : ''}`}
              onClick={() => debounceUserSets({ puzzleSetCat: 'user' })}
            >
              Внешний вид
            </button>
            <button
              className={`puzzle-category-btn ${user.puzzleSetCat === 'game' ? 'active' : ''}`}
              onClick={() => debounceUserSets({ puzzleSetCat: 'game' })}
            >
              Игровые
            </button>
          </div>
        </div>
        
        {/* Контент категории */}
        <div className="puzzle-settings-content">
          {user.puzzleSetCat === 'user' ? (
            <div className="puzzle-settings-list">
              {userSettingsConfig.map(setting => (
                <SettingItem
                  key={setting.key}
                  setting={setting}
                  currentSettings={currentUserSettings}
                  handleChange={handleUserSettingsChange}
                  openColorPicker={startColorPick}
                />
              ))}
            </div>
          ) : (
            <div className="puzzle-settings-list">
              {telegram.activeUserId === game.user_key ? (
                gameSettingsConfig.map(setting => (
                  <SettingItem
                    key={setting.key}
                    setting={setting}
                    currentSettings={currentGameSettings}
                    handleChange={handleGameSettingsChange}
                    openColorPicker={startColorPick}
                  />
                ))
              ) : (
                <> {/* Используем Fragment для группировки */}
                  <span className="puzzle-settings-owner-only">Эти настройки доступны только для создателя пазла.</span>
                  <div className="puzzle-settings-read-only-list">
                    <h4>Текущие настройки игры:</h4>
                    {gameSettingsConfig.map(setting => {
                      // Проверяем условия и для read-only отображения
                      if (setting.conditions) {
                        let shouldDisplay = true;
                        for (const key in setting.conditions) {
                          if (!checkCondition(setting.conditions[key], currentGameSettings[key])) {
                            shouldDisplay = false;
                            break;
                          }
                        }
                        if (!shouldDisplay) return null;
                      }

                      const value = currentGameSettings[setting.actualKey || setting.key] ?? setting.default ?? setting.min;

                      return (
                        <div key={`${setting.key}-readonly`} className="puzzle-setting-read-only-item">
                          <span className="puzzle-setting-read-only-label">{setting.label}:</span>
                          <span className="puzzle-setting-read-only-value">
                            {formatReadOnlyValue(setting, value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>


        <div className="button-row">
          <button
            className={gameChanged ? "small-button" : "wide-button"}
            onClick={handleCloseMenu}
          >
            Закрыть
          </button>
          {gameChanged && (
            <button
              className="small-red-button"
              onClick={handleReinitialize}
            >
              Перерисовать
            </button>
          )}

          </div>

               {/* Новая кнопка-ссылка на бота */}
               <div className="button-row">
           <button
            className="wide-button"
            // Используем переменную темы для красного цвета (обычно это #d14e4e или подобный)
            style={{ backgroundColor: 'var(--tg-theme-bg-color-transparent)' }}
            onClick={() => window.open('https://t.me/PuzzleTalesBot?startgroup=r--bot-app-appResults', '_blank')}
          >
            ➕ Добавить Пазлыча
          </button>
        </div>
      </div>

      {/* <<< Оверлей и колорпикер >>> */}
      {colorPickerState.isOpen && (
          <>
            {/* Оверлей для закрытия пикера */}
            <div className="puzzle-settings-overlay picker-overlay" onClick={endColorPick}></div>
            <div className="puzzle-color-picker-container">
              <HexAlphaColorPicker
                color={pickedColor}
                onChange={onColorPicking}
              />
              <button className="picker-apply-button" onClick={endColorPick}>
                Применить
              </button>
            </div>
          </>
      )}
    </>
  );
}

export default PuzzleSettings;